import { useState } from "react";
import { motion } from "framer-motion";
import {
  Compass, ScanSearch, Layers, FileText, BarChart3, ShieldCheck, GraduationCap, Users, ArrowUpRight,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Reveal, Eyebrow } from "./Reveal";

const SERVICES = [
  {
    slug: "regulatory-advisory",
    title: "Regulatory Advisory Services",
    icon: Compass,
    blurb: "Evolving mandates translated into decision-ready insight.",
    points: [
      ["Risk Interpretation", "Translate evolving mandates into clear business risks and decision-ready insights."],
      ["Impact Assessment", "Assess regulatory requirements, data and operational demands, and control implications across functions."],
      ["Change Monitoring", "Track regulatory updates, brief stakeholders and trigger proactive programme adjustments."],
      ["Actionable Roadmaps", "Prioritised plans, owners and milestones aligned to regulatory timelines."],
    ],
  },
  {
    slug: "risk-gap-analysis",
    title: "Risk Assessment & Gap Analysis",
    icon: ScanSearch,
    blurb: "A clear-eyed view of strengths, weaknesses, opportunities and threats.",
    points: [
      ["Strengths", "Robust internal controls and well-documented compliance policies provide a solid foundation for regulatory adherence."],
      ["Weaknesses", "Outdated procedures and inconsistent policy enforcement create vulnerabilities and increase risk exposure."],
      ["Opportunities", "Advanced compliance processes, technology and regular training can further reduce breach risks."],
      ["Threats", "Evolving regulations and emerging cyber threats may expose new compliance gaps if not proactively addressed."],
    ],
  },
  {
    slug: "program-implementation",
    title: "Compliance Program Implementation",
    icon: Layers,
    blurb: "Frameworks built around your risk profile and operating reality.",
    points: [
      ["Tailored Program Design", "Align frameworks to regulatory scope, risk profile and operating realities."],
      ["Control Establishment", "Define policies, procedures and automated checks with clear ownership and accountability."],
      ["Monitoring & Assurance", "Implement continuous monitoring, issue management and periodic testing."],
      ["Reporting & Outcomes", "Establish workflows for metrics and audits that reduce violations and cycle time."],
    ],
  },
  {
    slug: "policy-development",
    title: "Policy & Procedure Development",
    icon: FileText,
    blurb: "Documentation that stands up to regulatory scrutiny.",
    points: [
      ["Compliance Documentation", "Policies and procedures that meet regulations and reflect best practice, enabling clarity and accountability across teams."],
      ["Structured Policy Framework", "Well-structured documents standardise processes, provide clear guidance and ensure consistent execution across business units."],
      ["Risk Reduction & Oversight", "Robust documentation reduces operational risk and demonstrates due diligence to regulators, strengthening governance and trust."],
    ],
  },
  {
    slug: "regulatory-reporting",
    title: "Regulatory Reporting",
    icon: BarChart3,
    blurb: "Filings that surface credit, conduct, liquidity and solvency risk before it becomes systemic.",
    points: [
      ["Data Collection", "Aggregate and validate source data against regulatory definitions."],
      ["Report Preparation", "Map fields, reconcile figures and assemble filings per regulator formats."],
      ["Submission & Deadlines", "File to authorities on time with evidence of approvals and controls."],
      ["Documentation Readiness", "Organised source trails and controls for rapid audit access."],
      ["Mock Audits & Remediation", "Test controls, identify gaps and advise corrective actions before audits."],
    ],
  },
  {
    slug: "onsite-reviews",
    title: "Regulatory Onsite Reviews & Investigations",
    icon: ShieldCheck,
    blurb: "Prepared teams, prepared documentation — whatever the review type.",
    points: [
      ["Review Types", "Routine, cause-based, thematic and desk-based reviews each carry distinct documentation and preparedness requirements."],
      ["Staff Preparation", "Preparing teams for interaction with regulatory supervisors, interview sessions and post-review debriefs."],
      ["Regulator Responses", "Structured responses to post-review requests and findings from the regulator."],
      ["Focus Areas", "Risk management, operational resilience, consumer protection and compliance with regulatory frameworks."],
    ],
  },
  {
    slug: "training-education",
    title: "Compliance Training & Staff Education",
    icon: GraduationCap,
    blurb: "Role-based learning that measurably improves adherence.",
    points: [
      ["Consultant-Led Programmes", "Structured training ensures all levels understand obligations, ethics and risk-mitigation procedures through clear, role-based guidance."],
      ["Scenario-Based Learning", "Interactive workshops and e-learning modules improve retention and reduce inadvertent breaches through real-world practice."],
      ["Measured Impact", "Regular training correlates with a 25% improvement in policy adherence across organisations."],
    ],
  },
  {
    slug: "mentoring-coaching",
    title: "Mentoring & Coaching",
    icon: Users,
    blurb: "Capability, confidence and long-term career growth for compliance functions.",
    points: [
      ["Regulatory & Compliance Coaching", "Specialist coaching for Compliance, Legal, Risk and Conduct professionals — building confidence in regulatory interpretation, horizon scanning and practical application."],
      ["Organisational Mentoring Programmes", "Frameworks that build strong internal talent pipelines, promote diversity and inclusion, improve engagement, enhance succession planning and support leadership development at all levels."],
    ],
  },
];

export default function Services() {
  const [active, setActive] = useState(null);

  return (
    <section id="services" data-testid="services-section" className="relative py-28 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <Eyebrow>Core Consulting Practice</Eyebrow>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
            <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl text-[#1b2a4e] tracking-tight max-w-xl leading-[1.1]">
              Eight disciplines. <span className="italic text-[#a8824f]">One standard:</span> regulatory excellence.
            </h2>
            <p className="text-[#45506b] max-w-sm text-sm sm:text-base leading-relaxed">
              Select a practice area to see exactly what ComplyWise Consult delivers for your firm.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal key={s.slug} delay={(i % 4) * 0.08}>
                <motion.button
                  data-testid={`service-card-${s.slug}`}
                  onClick={() => setActive(s)}
                  whileHover={{ y: -6 }}
                  transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                  className="group relative w-full h-full text-left p-6 rounded-2xl bg-white border border-[#e6e1d5] hover:border-[#a8824f]/60 hover:shadow-[0_20px_50px_rgba(27,42,78,0.1)] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#a8824f]"
                >
                  <div className="flex items-start justify-between mb-6">
                    <span className="p-2.5 rounded-xl bg-[#faf9f6] border border-[#e6e1d5] text-[#1b2a4e] group-hover:text-[#a8824f] transition-colors duration-300">
                      <Icon size={20} strokeWidth={1.8} />
                    </span>
                    <ArrowUpRight
                      size={16}
                      className="text-[#c8cdd8] group-hover:text-[#a8824f] transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    />
                  </div>
                  <span className="font-mono-alt text-[10px] uppercase tracking-[0.25em] text-[#a8824f] block mb-2">
                    0{i + 1}
                  </span>
                  <h3 className="font-display text-xl text-[#1b2a4e] leading-snug mb-3">{s.title}</h3>
                  <p className="text-sm text-[#45506b] leading-relaxed">{s.blurb}</p>
                </motion.button>
              </Reveal>
            );
          })}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          data-testid="service-dialog"
          className="bg-white border-[#e6e1d5] text-[#1b2a4e] max-w-2xl max-h-[85vh] overflow-y-auto"
        >
          {active && (
            <>
              <DialogHeader>
                <p className="font-mono-alt text-[10px] uppercase tracking-[0.3em] text-[#a8824f] mb-2">
                  ComplyWise Consult · Practice Area
                </p>
                <DialogTitle className="font-display text-2xl sm:text-3xl text-[#1b2a4e]">
                  {active.title}
                </DialogTitle>
                <DialogDescription className="text-[#45506b] text-sm pt-1">
                  {active.blurb}
                </DialogDescription>
              </DialogHeader>
              <div className="mt-6 space-y-5">
                {active.points.map(([t, d], idx) => (
                  <div key={t} className="flex gap-4">
                    <span className="font-mono-alt text-xs text-[#a8824f] pt-0.5 shrink-0">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <h4 className="text-[#1b2a4e] font-semibold text-sm mb-1">{t}</h4>
                      <p className="text-[#45506b] text-sm leading-relaxed">{d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
